import { Box, Card, CardContent, Button } from "@mui/material";
import { useState, ChangeEvent, useRef } from "react";
import api from "../../services/ApiService";
import { ApiResponse } from "../../models/ApiResponse";
import { toast } from "react-toastify";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import * as XLSX from 'xlsx';
import InfoIcon from '@mui/icons-material/Info';
import FileDownload from "../../components/FileDownload";
import Loader from "../../components/Loader";
import { XLSX_MIME_TYPE } from "./EditItem/Constants";

const BulkUpload = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [fileData, setFileData] = useState<any[]>([]);
  const [columnData, setColumnData] = useState<any[]>([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const handleFileChange = async (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    setSelectedFile(file);
  };

  const handleClear = () => {
    setIsSuccess(false);
    setSelectedFile(null);
    setColumnData([]);
    setFileData([]);
    setErrorMessage("");
    setIsError(false);
    fileInputRef.current.value = "";
  };
  const fileInputRef = useRef(null);

  const handleBrowseClick = () => {
    setIsSuccess(false);
    setColumnData([]);
    setFileData([]);
    setErrorMessage("");
    setIsError(false);
    fileInputRef.current.click();
  };
  const handleSubmit = async () => {
    const maxSizeInBytes = 5 * 1024 * 1024; // 5MB
    const formData = new FormData();
    if (selectedFile?.type !== XLSX_MIME_TYPE) {
      return toast.error("Only XLSX files are supported.");
    }
    if (selectedFile.size < maxSizeInBytes) {
      formData.append("file", selectedFile); // Assuming 'file' is the key expected by the server
      api
        .post<ApiResponse<any>>("/Item/bulk-create", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then((response) => {
          if (response.isSuccess) {
            setSelectedFile(null);
            setIsSuccess(true);
            toast.success(response.message);
            fileInputRef.current.value = "";
          }
        })
        .catch((error) => {
          const errorResponse = error?.response?.data;

          if (errorResponse?.errors?.length > 0 || errorResponse?.data?.skuValidationErrors?.length > 0) {
            const reader = new FileReader();

            reader.onload = (e) => {
              const result = e.target?.result;
              if (!(result instanceof ArrayBuffer)) {
                console.error("Invalid file format");
                return;
              }

              const data = new Uint8Array(result);
              const workbook = XLSX.read(data, { type: 'array' });

              const sheet = workbook.Sheets['Products'];

              // sheet_to_json automatically reads headers from the first row
              let rowData: any[] = XLSX.utils.sheet_to_json(sheet, {
                defval: "",
                raw: false,
              });

              if (rowData.length && Object.values(rowData[rowData.length - 1]).every(val => val === "")) {
                rowData = rowData.slice(0, -1);
              }

              // 1. Handle SKU Validation Errors
              if (errorResponse?.data?.skuValidationErrors?.length > 0) {
                const skuErrorsData = errorResponse.data.skuValidationErrors.map((item: any, index: number) => ({
                  ...item,
                  id: index + 1,
                }));

                const skuColumns = [
                  { field: "code", headerName: "Code", width: 300 },
                  { field: "developmentType", headerName: "Development Type", width: 300 },
                  { field: "message", headerName: "Message", width: 800 },
                ];

                setColumnData(skuColumns);
                setFileData(skuErrorsData);
              }

              // 2. Handle General Row Errors
              else if (errorResponse?.errors?.length > 0) {
                let errorRowData;

                if (errorResponse.message.includes("Paired CVL ")) {
                  errorRowData = rowData.filter((_: any, index: number) =>
                    errorResponse.errors.includes(String(index + 1))
                  );
                } else {
                  errorRowData = rowData.filter((item: any) =>
                    errorResponse.errors.includes(item?.DevelopmentType) ||
                    errorResponse?.errors?.includes(item?.Stockcode)
                  );
                }

                if (errorRowData?.length > 0) {
                  const columns = Object.keys(errorRowData[0]).map((field) => ({
                    field: field === "PrjCode" ? "id" : field,
                    headerName: field,
                    width: 160,
                  }));

                  const convertedData = errorRowData.map((item, index) => {
                    const updatedItem = { ...item, id: index + 1 };
                    delete updatedItem.Id;
                    return updatedItem;
                  });

                  setColumnData(columns);
                  setFileData(convertedData);
                }
              }

              fileInputRef.current.value = "";
              setErrorMessage(errorResponse.message);
              setSelectedFile(null);
              setIsSuccess(false);
              setIsError(true);
            };

            reader.readAsArrayBuffer(selectedFile);
          }
          console.error("Exception from create file", error);
        });
    } else {
      toast.error("Upload file less than 5MB");
    }
  };

  const handleDownloadCVLFields = async () => {
    const fileDownload = await FileDownload({
      requestInput: null,
      inputFileName: 'CVLs_Values_',
      inputFileFormat: 'CVL Fields',
      setLoading,
    });
    await fileDownload.handleDownload();
  };

  return (
    <Card sx={{ marginBottom: 1 }}>
      <Loader isLoading={loading} />
      <CardContent>
        <div
          style={{
            color: "#454545",
            padding: "20px",
            borderBottom: "1px solid #ccc",
            fontSize: "20px",
            fontStyle: "normal",
            fontWeight: "400",
            lineHeight: "normal",
          }}
        >
          <p style={{ margin: "0px" }}>Bulk Product Create </p>
          <div style={{ display: "flex", alignItems: "start" }}>
            <div>
              <div style={{ display: "flex", alignItems: "center" }}>
                <InfoIcon sx={{ fontSize: 18, marginRight: "2px" }} />
                <p style={{ fontSize: "12px", margin: "0" }}>Notes:</p>
              </div>
              <ul style={{ fontSize: "12px", marginTop: "4px", paddingLeft: "20px" }}>
                <li>The Excel file must include a worksheet named <b>"Products"</b>; otherwise, the system will not allow it to proceed.</li>
                <li><b>Mandatory Fields:</b> ShortDesc, BrandCollection, Stage, Status, Category, Function, DevelopmentType</li>
                <li><b>Dependent Fields:</b> If dependent fields contain invalid values, the system will show a validation message.
                  <ul>
                    <li><b>Status</b> depends on <b>Stage</b></li>
                    <li><b>Function</b> depends on <b>Category</b></li>
                  </ul>
                </li>
                <li>For all <b>CVL (dropdown)</b> fields, the value must match one of the allowed CVL options exactly. Any incorrect or unknown values will be replaced with blank.</li>
              </ul>
            </div>
          </div>
        </div>

        <Box sx={{ height: 1, backgroundColor: "#ccc" }} />

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "flex-start",
            padding: "20px",
          }}
        >
          <input
            type="file"
            id="fileInput"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
            accept=".xlsx"
          />
          <div
            className="inputWrap"
            style={{ width: "40%", marginRight: "10px", maxWidth: "100%" }}
          >
            <input
              type="text"
              name="fileName"
              id="fileName"
              value={selectedFile ? selectedFile.name : ""}
              style={{
                width: "100%",
                height: "35px",
              }}
              readOnly
            />

            <p
              style={{
                color: "#717171",
                fontSize: "15px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "normal",
                letterSpacing: "0.17px",
              }}
            >
              XLSX (max. 5MB).
              <span
                onClick={handleDownloadCVLFields}
                style={{ cursor: "pointer", paddingLeft: "10px" }}
              >
                <strong>[Download CVL Fields]</strong>
              </span>
            </p>
          </div>

          <Button
            onClick={handleBrowseClick}
            variant="contained"
            style={{
              borderRadius: "0px",
              border: "0.5px solid grey",
              width: "150px",
              height: "35px",
              marginRight: "10px",
            }}
          >
            Browse
          </Button>

          <Button
            onClick={handleSubmit}
            style={{
              borderRadius: "0px",
              border: "0.5px solid grey",
              width: "150px",
              height: "35px",
              marginRight: "10px",
            }}
            disabled={selectedFile ? false : true}
          >
            Submit
          </Button>
          <Button
            onClick={handleClear}
            style={{
              borderRadius: "0px",
              border: "0.5px solid grey",
              width: "150px",
              height: "35px",
            }}
          >
            Clear
          </Button>
        </Box>
        {isSuccess ? (
          <>
            <div
              style={{
                color: "green",
                padding: "20px",
                borderBottom: "1px solid #ccc",
                fontSize: "20px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "normal",
              }}
            >
              <p style={{ margin: "0px" }}>
                File has been uploaded successfully
              </p>
            </div>
          </>
        ) : (
          ""
        )}
        {isError && (
          <>
            <div
              style={{
                color: "red",
                padding: "20px",
                borderBottom: "1px solid #ccc",
                fontSize: "20px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "normal",
              }}
            >
              <p style={{ margin: "0px" }}>{errorMessage}</p>
            </div>
            <Box className="bulk-create-grid-cell" sx={{ height: "100%", width: "100%" }}>
              {fileData.length > 0 && (
                <DataGrid
                  rows={fileData}
                  columns={columnData}
                  slots={{ toolbar: GridToolbar }}
                  slotProps={{
                    toolbar: {
                      showQuickFilter: true,
                    },
                  }}
                  disableColumnFilter
                  disableColumnSelector
                  disableDensitySelector
                  disableRowSelectionOnClick={true}
                  getRowHeight={() => 'auto'}
                />
              )}
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};
export default BulkUpload;
